import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { staffClient } from 'clients/staff';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import FilterSection from 'ui/FilterSection';
import TableContainer from 'ui/TableContainer';
import SearchInput from 'ui/SearchInput';
import Button from 'ui/Button';
import FilterCard, { FilterCardGroup } from 'ui/FilterCard';
import { Badge } from 'ui/shadcn';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface QualificationRow {
    id: number;
    staffId: number;
    staffName: string;
    supervisorGrade: string;
    supervisorCertificateNumber: string;
    technicalQualification: string;
    acquisitionDate: string;
}

interface QualificationStats {
    total: number;
    electricPE: number;
    firePE: number;
    telecomPE: number;
    electricEng: number;
    fireEng: number;
    telecomEng: number;
}

const QUALIFICATION_OPTIONS = [
    { value: '', label: '전체' },
    { value: '전기기술사', label: '전기기술사' },
    { value: '소방기술사', label: '소방기술사' },
    { value: '정보통신기술사', label: '정보통신기술사' },
    { value: '전기기사', label: '전기기사' },
    { value: '소방설비기사(기계)', label: '소방설비기사(기계)' },
    { value: '소방설비기사(전기)', label: '소방설비기사(전기)' },
    { value: '정보통신기사', label: '정보통신기사' },
];

const QualificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<QualificationRow[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterQualification, setFilterQualification] = useState(searchParams.get('filter') || '');
    const [stats, setStats] = useState<QualificationStats>({
        total: 0, electricPE: 0, firePE: 0, telecomPE: 0,
        electricEng: 0, fireEng: 0, telecomEng: 0
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await staffClient.get('/staffs', { params: { limit: 1000 } });
            const staffs = response.data?.data || [];

            const allRows: QualificationRow[] = [];
            const newStats: QualificationStats = {
                total: 0, electricPE: 0, firePE: 0, telecomPE: 0,
                electricEng: 0, fireEng: 0, telecomEng: 0
            };

            staffs.forEach((staff: any) => {
                if (staff.qualifications && Array.isArray(staff.qualifications)) {
                    staff.qualifications.forEach((qual: any) => {
                        const tq = qual.technical_qualification || '';
                        allRows.push({
                            id: qual.id,
                            staffId: staff.id,
                            staffName: staff.name,
                            supervisorGrade: qual.supervisor_grade || '',
                            supervisorCertificateNumber: qual.supervisor_certificate_number || '',
                            technicalQualification: tq,
                            acquisitionDate: qual.acquisition_date || '',
                        });

                        newStats.total++;
                        if (tq === '전기기술사') newStats.electricPE++;
                        if (tq === '소방기술사') newStats.firePE++;
                        if (tq === '정보통신기술사') newStats.telecomPE++;
                        if (tq === '전기기사') newStats.electricEng++;
                        if (tq === '소방설비기사(기계)' || tq === '소방설비기사(전기)') newStats.fireEng++;
                        if (tq === '정보통신기사') newStats.telecomEng++;
                    });
                }
            });

            setRows(allRows);
            setStats(newStats);
        } catch (error) {
            console.error('Failed to fetch qualifications:', error);
            toast.error('자격 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleExcelDownload = () => {
        const data = filteredRows.map((row, idx) => ({
            'No': idx + 1,
            '직원명': row.staffName,
            '감리원 등급': row.supervisorGrade || '',
            '감리원 증번호': row.supervisorCertificateNumber || '',
            '기술자격': row.technicalQualification || '',
            '취득일': row.acquisitionDate ? new Date(row.acquisitionDate).toLocaleDateString('ko-KR') : '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '자격현황');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const filterLabel = filterQualification || '전체';
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), `자격현황_${filterLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const filteredRows = rows.filter((row) => {
        const matchesSearch = !searchTerm ||
            row.staffName.includes(searchTerm) ||
            row.technicalQualification.includes(searchTerm) ||
            row.supervisorCertificateNumber.includes(searchTerm);
        const matchesFilter = !filterQualification ||
            row.technicalQualification === filterQualification;
        return matchesSearch && matchesFilter;
    });

    const columns: Column<QualificationRow>[] = [
        {
            key: 'staffName',
            header: '직원명',
            width: '120px',
            sortable: true,
            render: (value, row) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => navigate(`/admin/staffs/${row.staffId}?tab=qualification`)}
                >
                    {value}
                </button>
            ),
        },
        {
            key: 'supervisorGrade',
            header: '감리원 등급',
            width: '120px',
            sortable: true,
        },
        {
            key: 'supervisorCertificateNumber',
            header: '감리원 증번호',
            width: '150px',
            render: (value) => <span className="tabular-nums">{value}</span>,
        },
        {
            key: 'technicalQualification',
            header: '기술자격',
            width: '160px',
            sortable: true,
            render: (value) => {
                if (!value) return '-';
                // 공종별 색 대신 등급만 구분: 기술사 = navy, 기사 = 무채색
                const isProfessionalEngineer = value.endsWith('기술사');
                return (
                    <Badge
                        variant={isProfessionalEngineer ? 'default' : 'secondary'}
                        className="whitespace-nowrap"
                    >
                        {value}
                    </Badge>
                );
            },
        },
        {
            key: 'acquisitionDate',
            header: '취득일',
            width: '120px',
            sortable: true,
            render: (value) =>
                value ? (
                    <span className="tabular-nums">{new Date(value).toLocaleDateString('ko-KR')}</span>
                ) : (
                    '-'
                ),
        },
    ];

    if (loading) {
        return (
            <PageContainer>
                <Loading text="자격 현황을 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="자격 현황"
                subtitle="전 직원의 자격 보유 현황을 확인합니다"
            />

            <FilterCardGroup columns={4}>
                {[
                    { key: '', label: '전체', count: stats.total, suffix: '건' },
                    { key: '전기기술사', label: '전기기술사', count: stats.electricPE, suffix: '명' },
                    { key: '소방기술사', label: '소방기술사', count: stats.firePE, suffix: '명' },
                    { key: '정보통신기술사', label: '정보통신기술사', count: stats.telecomPE, suffix: '명' },
                ].map((card) => (
                    <FilterCard
                        key={card.key}
                        label={card.label}
                        count={card.count}
                        suffix={card.suffix}
                        active={filterQualification === card.key}
                        onClick={() => setFilterQualification(card.key)}
                    />
                ))}
            </FilterCardGroup>

            <FilterSection>
                <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="직원명, 자격명, 증번호 검색..."
                />
            </FilterSection>

            <TableContainer
                title="자격 목록"
                resultCount={filteredRows.length}
                actions={
                    <Button variant="outline" size="small" icon={<Download />} onClick={handleExcelDownload}>
                        엑셀
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    data={filteredRows}
                    pagination
                    pageSize={20}
                    defaultSortKey="staffName"
                    defaultSortOrder="asc"
                    stickyHeader
                    maxHeight="600px"
                    emptyMessage="등록된 자격 정보가 없습니다."
                />
            </TableContainer>
        </PageContainer>
    );
};

export default QualificationsPage;
